import { authManager } from '../auth-manager.js';
import { openQRModal } from './qr-modal.js';

/**
 * Login View — Google OAuth 2.0 Social Login
 * 
 * Uses Google Identity Services (GIS) SDK to render the official
 * Google Sign-In button. On click, Google handles the entire OAuth
 * popup flow (including 2FA if the user has it enabled on their
 * Google Account). We receive a signed ID Token which is sent to
 * our backend for cryptographic verification.
 */
export function renderLoginView(container) {
  container.innerHTML = `
    <div class="login-wrapper">
      
      <!-- Brand Card -->
      <div class="glass-panel login-card">
        
        <div class="login-brand-icon" style="background: #F8FAFC; border: 1px solid var(--color-border); padding: 0.5rem; display: flex; align-items: center; justify-content: center; width: 68px; height: 68px; border-radius: 1.25rem; margin: 0 auto 1.25rem auto;">
          <img src="/favicon.svg" alt="NF Tracker Logo" style="width: 48px; height: 48px; border-radius: 0.75rem;" />
        </div>

        <div style="display: flex; align-items: center; justify-content: center; gap: 0.45rem; margin-bottom: 0.25rem;">
          <h1 class="login-title" style="margin: 0;">NF Tracker</h1>
          <span class="badge-beta">Beta</span>
        </div>
        <p class="login-subtitle">
          Controle Inteligente de Gastos Fiscais através da NFC-e
        </p>

        <!-- Google Sign-In button rendered by GIS SDK -->
        <div id="gsi-button-container" class="login-google-container">
          <div class="login-loading">
            <span class="material-symbols-outlined spin">progress_activity</span>
            <span>Carregando Google Sign-In...</span>
          </div>
        </div>

        <!-- Mobile Access QR Code Button -->
        <button id="login-qr-btn" class="login-qr-btn">
          <span class="material-symbols-outlined" style="font-size: 20px;">qr_code_2</span>
          Acesso Rápido Mobile (QR Code)
        </button>

        <!-- Status/error messages -->
        <div id="login-status" class="login-status" style="display: none; margin-top: 1rem;"></div>

        <div class="login-footer">
          <div style="margin-bottom: 0.4rem;">
            <span class="material-symbols-outlined" style="font-size: 14px; vertical-align: middle; color: #10B981;">verified_user</span>
            Seus dados são protegidos e mantidos sob sua propriedade.
          </div>
          <div style="font-size: 0.75rem; color: #64748B; line-height: 1.45; border-top: 1px solid var(--color-border); padding-top: 0.5rem; margin-top: 0.5rem;">
            <span>Aplicativo para uso pessoal. A responsabilidade pelo uso e pelos dados inseridos é exclusivamente do usuário.</span>
          </div>
        </div>
      </div>

    </div>
  `;

  const statusEl = container.querySelector('#login-status');
  const gsiContainer = container.querySelector('#gsi-button-container');

  const qrBtn = container.querySelector('#login-qr-btn');
  if (qrBtn) {
    qrBtn.addEventListener('click', () => openQRModal());
  }

  // ——— Guest Login ———
  const guestBtn = container.querySelector('#guest-login-btn');
  if (guestBtn) {
    guestBtn.addEventListener('click', () => {
      authManager.setSession('guest-token-' + Date.now(), {
        name: 'Convidado',
        email: 'convidado@nfce.local',
        picture: ''
      });
      window.location.hash = '#notas';
    });
  }

  // ——— Google Identity Services (GIS) ———
  initGoogleSignIn(gsiContainer, statusEl);
}

/**
 * Initialize the Google Identity Services SDK.
 * Waits for the SDK to load, fetches the Client ID from the backend,
 * and renders the official Google Sign-In button.
 */
async function initGoogleSignIn(buttonContainer, statusEl) {
  // 1. Fetch Google Client ID from backend
  const clientId = await authManager.getGoogleClientId();

  if (!clientId) {
    showStatus(statusEl, 'warn',
      '⚠️ Google Client ID não configurado no servidor. ' +
      'Configure a variável GOOGLE_CLIENT_ID no backend.'
    );
    buttonContainer.innerHTML = `
      <button class="login-google-fallback-btn" disabled style="opacity: 0.5; cursor: not-allowed;">
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
        </svg>
        Entrar com o Google (não configurado)
      </button>
    `;
    return;
  }

  // 2. Wait for Google Identity Services SDK to load
  try {
    await waitForGIS(8000);
  } catch (err) {
    showStatus(statusEl, 'error', '❌ Não foi possível carregar o Google Sign-In. Verifique sua conexão.');
    buttonContainer.innerHTML = '';
    return;
  }

  // 3. Initialize GIS and render the official Google button
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: handleGoogleCallback,
    auto_select: false,
    cancel_on_tap_outside: true,
    context: 'signin',
    ux_mode: 'popup',
    itp_support: true,
  });

  // Clear loading indicator and render official Google button
  buttonContainer.innerHTML = '<div id="gsi-btn"></div>';
  const btnEl = buttonContainer.querySelector('#gsi-btn');

  window.google.accounts.id.renderButton(btnEl, {
    theme: 'filled_black',
    size: 'large',
    shape: 'rectangular',
    text: 'signin_with',
    locale: 'pt-BR',
    width: Math.min(buttonContainer.offsetWidth, 380),
    logo_alignment: 'left',
  });

  // Also prompt One Tap if available
  window.google.accounts.id.prompt((notification) => {
    if (notification.isNotDisplayed()) {
      console.log('One Tap not displayed:', notification.getNotDisplayedReason());
    }
  });
}

/**
 * Callback from Google Identity Services.
 * Receives the credential (JWT ID Token) and sends it to the backend.
 */
async function handleGoogleCallback(response) {
  const statusEl = document.querySelector('#login-status');

  if (!response.credential) {
    showStatus(statusEl, 'error', '❌ Falha na autenticação com o Google.');
    return;
  }

  showStatus(statusEl, 'info', '🔄 Verificando credenciais...');

  const result = await authManager.verifyGoogleCredential(response.credential);

  if (result.success) {
    showStatus(statusEl, 'success', `✅ Bem-vindo, ${result.user.name}!`);
    setTimeout(() => {
      window.location.hash = '#notas';
    }, 400);
  } else {
    showStatus(statusEl, 'error', `❌ ${result.error}`);
  }
}

/**
 * Wait for the Google Identity Services SDK to be available.
 */
function waitForGIS(timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const interval = 100;
    let elapsed = 0;
    const check = setInterval(() => {
      elapsed += interval;
      if (window.google?.accounts?.id) {
        clearInterval(check);
        resolve();
      } else if (elapsed >= timeoutMs) {
        clearInterval(check);
        reject(new Error('GIS SDK timeout'));
      }
    }, interval);
  });
}

/**
 * Display a status message in the login view.
 */
function showStatus(el, type, message) {
  if (!el) return;
  el.style.display = 'block';
  el.className = `login-status login-status-${type}`;
  el.textContent = message;
}
